import React, { useState } from 'react';
import { 
  X, 
  Users, 
  UserPlus, 
  Shield, 
  Lock, 
  Unlock, 
  Check, 
  Copy, 
  Share2, 
  Trash2, 
  PauseCircle, 
  PlayCircle, 
  Sliders, 
  AlertCircle,
  EyeOff,
  Eye,
  CheckCircle2,
  ExternalLink
} from 'lucide-react';
import { 
  useTeamPermissions, 
  TeamPermissions, 
  TeamMember, 
  DEFAULT_PERMISSIONS_PRESETS 
} from '../../../contexts/TeamPermissionsContext';

interface TeamManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessName?: string;
}

export default function TeamManagementModal({
  isOpen,
  onClose,
  businessName = 'Sua Empresa'
}: TeamManagementModalProps) {
  const { 
    teamMembers, 
    teamSummary, 
    inviteMember, 
    updateMemberPermissions, 
    toggleMemberStatus, 
    removeMember,
    isLoadingTeam 
  } = useTeamPermissions();

  const [activeTab, setActiveTab] = useState<'list' | 'invite' | 'edit'>('list');
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  // Formulário de Convite / Edição
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('vendedor');
  const [permissions, setPermissions] = useState<TeamPermissions>(DEFAULT_PERMISSIONS_PRESETS.vendedor);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [generatedInviteLink, setGeneratedInviteLink] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  if (!isOpen) return null;

  const handleApplyPreset = (presetKey: string) => {
    setRole(presetKey);
    if (DEFAULT_PERMISSIONS_PRESETS[presetKey]) {
      setPermissions(DEFAULT_PERMISSIONS_PRESETS[presetKey]);
    }
  };

  const handleTogglePermission = (key: keyof TeamPermissions) => {
    setPermissions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleStartInvite = () => {
    setName('');
    setEmail('');
    setRole('vendedor');
    setPermissions(DEFAULT_PERMISSIONS_PRESETS.vendedor);
    setErrorMessage(null);
    setGeneratedInviteLink(null);
    setActiveTab('invite');
  };

  const handleStartEdit = (member: TeamMember) => {
    setSelectedMember(member);
    setName(member.name);
    setEmail(member.invite_email);
    setRole(member.role);
    setPermissions(member.permissions);
    setErrorMessage(null);
    setGeneratedInviteLink(null);
    setActiveTab('edit');
  };

  const handleSubmitInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setErrorMessage('Preencha o nome e o e-mail do colaborador.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    const result = await inviteMember(name, email, role, permissions);
    setIsSubmitting(false);

    if (result.success && result.token) {
      const link = `${window.location.origin}/convite/${result.token}`;
      setGeneratedInviteLink(link);
    } else {
      setErrorMessage(result.error || 'Falha ao convidar.');
    }
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMember) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    const ok = await updateMemberPermissions(selectedMember.id, permissions);
    setIsSubmitting(false);

    if (ok) {
      setActiveTab('list');
      setSelectedMember(null);
    } else {
      setErrorMessage('Erro ao salvar permissões.');
    }
  };

  const handleCopyLink = (linkToCopy: string) => {
    navigator.clipboard.writeText(linkToCopy);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 3000);
  };

  const getWhatsAppShareUrl = (link: string, memberName: string) => {
    const text = `Olá, ${memberName}! Você foi convidado para acessar a equipe da ${businessName} no R-MAX Precificação Inteligente. Crie seu acesso clicando no link: ${link}`;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  };

  return (
    <div className="fixed inset-0 z-100 bg-neutral-950/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 font-sans">
      <div className="w-full max-w-2xl bg-white border border-zinc-100 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        
        {/* Cabeçalho */}
        <div className="p-5 sm:p-6 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-brand/10 border border-brand/20 flex items-center justify-center text-brand">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base sm:text-lg font-black text-zinc-900">
                  Gestão de Equipe & Usuários
                </h2>
                <span className="text-[10px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full border border-emerald-200">
                  Premium
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-0.5">
                Até 10 usuários inclusos no seu plano com travas de segurança
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-full transition-colors cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Barra de Capacidade de Equipe */}
        <div className="px-6 py-3 bg-zinc-50 border-b border-zinc-100 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <span className="font-bold text-zinc-700">Capacidade da Equipe:</span>
            <span className="font-black text-brand bg-brand/10 px-2 py-0.5 rounded-md">
              {teamSummary.total_members} de {teamSummary.max_included_members} vagas utilizadas
            </span>
          </div>
          <span className="text-[11px] text-zinc-400 font-medium">
            {teamSummary.slots_remaining} vagas disponíveis
          </span>
        </div>

        {/* Conteúdo Principal */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">

          {/* ABA 1: LISTAGEM DE MEMBROS */}
          {activeTab === 'list' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-black text-zinc-900">Membros da sua Empresa</h3>
                  <p className="text-xs text-zinc-500">Defina o que cada colaborador pode ver e fazer.</p>
                </div>

                <button
                  type="button"
                  onClick={handleStartInvite}
                  disabled={teamSummary.slots_remaining <= 0}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black shadow-sm transition-all cursor-pointer ${
                    teamSummary.slots_remaining > 0
                      ? 'bg-brand text-white hover:bg-brand-dark'
                      : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                  }`}
                >
                  <UserPlus className="h-4 w-4" />
                  <span>Convidar Colaborador</span>
                </button>
              </div>

              {teamMembers.length === 0 ? (
                <div className="p-8 text-center bg-zinc-50 border border-zinc-100 rounded-3xl space-y-3">
                  <div className="w-12 h-12 rounded-2xl bg-zinc-200/60 text-zinc-500 flex items-center justify-center mx-auto">
                    <Users className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-zinc-800">Nenhum colaborador adicionado ainda</h4>
                    <p className="text-xs text-zinc-500 max-w-sm mx-auto mt-1">
                      Convide vendedores, técnicos ou atendentes para acelerar sua rotina sem expor seus custos e lucros.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleStartInvite}
                    className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-xl text-xs font-black hover:bg-brand-dark transition-all cursor-pointer shadow-sm"
                  >
                    <UserPlus className="h-4 w-4" />
                    <span>Convidar Primeiro Membro</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {teamMembers.map(member => {
                    const isSuspended = member.status === 'suspended';
                    const isPending = member.status === 'pending';
                    const inviteLink = `${window.location.origin}/convite/${member.invite_token}`;

                    return (
                      <div 
                        key={member.id} 
                        className={`p-4 rounded-2xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                          isSuspended 
                            ? 'bg-zinc-50/70 border-zinc-200 opacity-60' 
                            : 'bg-white border-zinc-100 hover:border-zinc-200 shadow-sm'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center font-black text-xs text-zinc-700 uppercase">
                            {member.name.substring(0, 2)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="text-xs font-black text-zinc-900">{member.name}</h4>
                              {isPending && (
                                <span className="text-[10px] font-bold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">
                                  Convite Pendente
                                </span>
                              )}
                              {isSuspended && (
                                <span className="text-[10px] font-bold bg-rose-50 text-rose-700 px-2 py-0.5 rounded-full border border-rose-200">
                                  Acesso Pausado
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-zinc-500 mt-0.5">
                              <span>{member.invite_email}</span>
                              <span>•</span>
                              <span className="capitalize font-bold text-brand">{member.role}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 self-end sm:self-center">
                          {isPending && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleCopyLink(inviteLink)}
                                title="Copiar link de convite"
                                className="p-2 hover:bg-zinc-100 text-zinc-600 rounded-xl transition-colors cursor-pointer border border-zinc-200"
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                              <a
                                href={getWhatsAppShareUrl(inviteLink, member.name)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Enviar via WhatsApp"
                                className="p-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl transition-colors cursor-pointer border border-emerald-200"
                              >
                                <Share2 className="h-3.5 w-3.5" />
                              </a>
                            </>
                          )}

                          <button
                            type="button"
                            onClick={() => handleStartEdit(member)}
                            title="Editar travas e permissões"
                            className="p-2 hover:bg-zinc-100 text-zinc-700 rounded-xl transition-colors cursor-pointer border border-zinc-200 inline-flex items-center gap-1 text-[11px] font-bold"
                          >
                            <Sliders className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Permissões</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => toggleMemberStatus(member.id, member.status)}
                            title={isSuspended ? "Reativar acesso" : "Pausar acesso temporariamente"}
                            className={`p-2 rounded-xl transition-colors cursor-pointer border ${
                              isSuspended
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                : 'bg-zinc-50 text-zinc-600 border-zinc-200 hover:bg-zinc-100'
                            }`}
                          >
                            {isSuspended ? <PlayCircle className="h-3.5 w-3.5" /> : <PauseCircle className="h-3.5 w-3.5" />}
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`Deseja realmente remover ${member.name} da sua equipe?`)) {
                                removeMember(member.id);
                              }
                            }}
                            title="Remover da equipe"
                            className="p-2 hover:bg-rose-50 text-rose-600 rounded-xl transition-colors cursor-pointer border border-rose-100"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ABA 2: CONVIDAR OU EDITAR PERMISSÕES */}
          {(activeTab === 'invite' || activeTab === 'edit') && (
            <form onSubmit={activeTab === 'invite' ? handleSubmitInvite : handleSaveEdit} className="space-y-6">
              
              {/* Topo do Formulário */}
              <div className="flex items-center justify-between pb-3 border-b border-zinc-100">
                <div>
                  <h3 className="text-sm font-black text-zinc-900">
                    {activeTab === 'invite' ? 'Novo Colaborador' : `Ajustar Permissões: ${name}`}
                  </h3>
                  <p className="text-xs text-zinc-500">
                    Defina quais telas e informações financeiras estarão liberadas ou travadas.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveTab('list')}
                  className="text-xs text-zinc-500 hover:text-zinc-800 font-bold cursor-pointer"
                >
                  Voltar para a lista
                </button>
              </div>

              {errorMessage && (
                <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold rounded-xl flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Se o convite acabou de ser gerado */}
              {generatedInviteLink && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl space-y-3">
                  <div className="flex items-center gap-2 text-emerald-800 font-black text-xs">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Convite gerado com sucesso!</span>
                  </div>
                  <p className="text-xs text-emerald-900 leading-relaxed">
                    Envie o link abaixo para o colaborador criar a senha dele e acessar o R-MAX da sua empresa:
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={generatedInviteLink}
                      className="flex-1 bg-white border border-emerald-200 px-3 py-2 rounded-xl text-xs font-mono text-zinc-700 outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => handleCopyLink(generatedInviteLink)}
                      className="px-3 py-2 bg-white hover:bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-bold transition-colors cursor-pointer flex items-center gap-1"
                    >
                      {copiedLink ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      <span>{copiedLink ? 'Copiado!' : 'Copiar'}</span>
                    </button>
                    <a
                      href={getWhatsAppShareUrl(generatedInviteLink, name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black transition-colors cursor-pointer flex items-center gap-1 shadow-sm"
                    >
                      <Share2 className="h-3.5 w-3.5" />
                      <span>WhatsApp</span>
                    </a>
                  </div>
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab('list');
                        setGeneratedInviteLink(null);
                      }}
                      className="text-xs font-black text-emerald-800 hover:underline cursor-pointer"
                    >
                      ← Voltar para a lista de colaboradores
                    </button>
                  </div>
                </div>
              )}

              {/* Dados Básicos */}
              {!generatedInviteLink && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-black text-zinc-700">Nome do Colaborador *</label>
                      <input
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="Ex: Carlos Silva"
                        required
                        className="w-full bg-zinc-50 border border-zinc-200 focus:border-brand px-3.5 py-2.5 rounded-xl text-xs outline-none"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-black text-zinc-700">E-mail do Colaborador *</label>
                      <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="carlos@gmail.com"
                        required
                        disabled={activeTab === 'edit'}
                        className="w-full bg-zinc-50 border border-zinc-200 focus:border-brand px-3.5 py-2.5 rounded-xl text-xs outline-none disabled:opacity-60"
                      />
                    </div>
                  </div>

                  {/* Seleção Rápida de Perfil / Cargo */}
                  <div className="space-y-2">
                    <label className="text-xs font-black text-zinc-700">
                      Função Recomendada (Preenche as travas automaticamente):
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        { key: 'vendedor', label: 'Vendedor / Orçamentista' },
                        { key: 'tecnico', label: 'Técnico / Operacional' },
                        { key: 'atendente', label: 'Recepção / Balcão' },
                        { key: 'gerente', label: 'Gerente Geral' },
                      ].map(p => (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => handleApplyPreset(p.key)}
                          className={`p-2.5 rounded-xl text-xs font-bold text-center border transition-all cursor-pointer ${
                            role === p.key
                              ? 'bg-brand/10 border-brand text-brand shadow-sm'
                              : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100'
                          }`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Matriz de Travas e Liberdades (Toggles) */}
                  <div className="space-y-3 pt-2">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-brand" />
                      <h4 className="text-xs font-black text-zinc-900 uppercase tracking-wider">
                        Matriz de Travas & Liberdades (Controle Fino)
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 gap-2.5">
                      
                      {/* Trava: Ver Margem de Lucro */}
                      <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-2xl flex items-center justify-between">
                        <div className="pr-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-black text-zinc-800">Visualizar Margem de Lucro Real</span>
                            {!permissions.can_view_margin && (
                              <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200">
                                🔒 Oculto (Segredo Comercial)
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-zinc-500 mt-0.5">
                            Se desativado, o colaborador vê apenas o preço final de venda, sem saber o custo e o lucro.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleTogglePermission('can_view_margin')}
                          className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer shrink-0 ${
                            permissions.can_view_margin ? 'bg-brand justify-end' : 'bg-zinc-300 justify-start'
                          }`}
                        >
                          <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
                        </button>
                      </div>

                      {/* Trava: Modificar Preço Calculado */}
                      <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-2xl flex items-center justify-between">
                        <div className="pr-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-black text-zinc-800">Alterar Preço Sugerido pela IA</span>
                            {!permissions.can_edit_final_price && (
                              <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                                🔒 Travado no Preço Calculado
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-zinc-500 mt-0.5">
                            Se desativado, impede o vendedor de dar descontos manuais que diminuam o lucro da empresa.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleTogglePermission('can_edit_final_price')}
                          className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer shrink-0 ${
                            permissions.can_edit_final_price ? 'bg-brand justify-end' : 'bg-zinc-300 justify-start'
                          }`}
                        >
                          <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
                        </button>
                      </div>

                      {/* Trava: Centro de Custos e Pró-Labore */}
                      <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-2xl flex items-center justify-between">
                        <div className="pr-3">
                          <span className="text-xs font-black text-zinc-800">Acesso ao Centro de Custos & Pró-labore</span>
                          <p className="text-[11px] text-zinc-500 mt-0.5">
                            Oculta custos fixos, folha de pagamento e retiradas dos sócios.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleTogglePermission('can_view_cost_center')}
                          className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer shrink-0 ${
                            permissions.can_view_cost_center ? 'bg-brand justify-end' : 'bg-zinc-300 justify-start'
                          }`}
                        >
                          <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
                        </button>
                      </div>

                      {/* Trava: Cockpit Executivo e Faturamento */}
                      <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-2xl flex items-center justify-between">
                        <div className="pr-3">
                          <span className="text-xs font-black text-zinc-800">Acesso ao Cockpit Executivo & DRE</span>
                          <p className="text-[11px] text-zinc-500 mt-0.5">
                            Oculta o faturamento acumulado e relatórios consolidados da diretoria.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleTogglePermission('can_view_executive_cockpit')}
                          className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer shrink-0 ${
                            permissions.can_view_executive_cockpit ? 'bg-brand justify-end' : 'bg-zinc-300 justify-start'
                          }`}
                        >
                          <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
                        </button>
                      </div>

                      {/* Trava: Excluir Registros */}
                      <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-2xl flex items-center justify-between">
                        <div className="pr-3">
                          <span className="text-xs font-black text-zinc-800">Permitir Excluir Orçamentos ou Clientes</span>
                          <p className="text-[11px] text-zinc-500 mt-0.5">
                            Se desativado, o colaborador só pode criar e consultar, sem apagar históricos.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleTogglePermission('can_delete_records')}
                          className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer shrink-0 ${
                            permissions.can_delete_records ? 'bg-brand justify-end' : 'bg-zinc-300 justify-start'
                          }`}
                        >
                          <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
                        </button>
                      </div>

                      {/* Trava: Exportar Carteira de Clientes */}
                      <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-2xl flex items-center justify-between">
                        <div className="pr-3">
                          <span className="text-xs font-black text-zinc-800">Exportar Clientes em Planilha (CSV/Excel)</span>
                          <p className="text-[11px] text-zinc-500 mt-0.5">
                            Blindagem contra vazamento de base e proteção em conformidade com a LGPD.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleTogglePermission('can_export_clients')}
                          className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer shrink-0 ${
                            permissions.can_export_clients ? 'bg-brand justify-end' : 'bg-zinc-300 justify-start'
                          }`}
                        >
                          <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
                        </button>
                      </div>

                      {/* Trava: Chave PIX (Protegida) */}
                      <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-2xl flex items-center justify-between">
                        <div className="pr-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-black text-zinc-800">Alterar Chave PIX da Empresa</span>
                            <span className="text-[10px] font-black text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full border border-rose-200">
                              Trava Financeira
                            </span>
                          </div>
                          <p className="text-[11px] text-zinc-500 mt-0.5">
                            Garante que os pagamentos dos clientes sempre caiam exclusivamente na conta oficial da empresa.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleTogglePermission('can_edit_pix')}
                          className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer shrink-0 ${
                            permissions.can_edit_pix ? 'bg-brand justify-end' : 'bg-zinc-300 justify-start'
                          }`}
                        >
                          <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
                        </button>
                      </div>

                      {/* Permissão: Emitir Recibos Financeiros */}
                      <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-2xl flex items-center justify-between">
                        <div className="pr-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-black text-zinc-800">Emitir Recibos Financeiros & Comprovantes PIX</span>
                            {permissions.can_issue_receipts && (
                              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                                ✓ Habilitado para Vendas
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-zinc-500 mt-0.5">
                            Permite gerar e compartilhar recibos profissionais após fechamento de orçamentos e serviços.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleTogglePermission('can_issue_receipts')}
                          className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer shrink-0 ${
                            permissions.can_issue_receipts ? 'bg-brand justify-end' : 'bg-zinc-300 justify-start'
                          }`}
                        >
                          <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
                        </button>
                      </div>

                      {/* Permissão: Visualizar Todos os Pedidos e OS */}
                      <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-2xl flex items-center justify-between">
                        <div className="pr-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-black text-zinc-800">Visualizar Todos os Pedidos & Ordens de Serviço</span>
                            {permissions.can_view_all_work_orders && (
                              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                                ✓ Visão Operacional
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-zinc-500 mt-0.5">
                            Se desativado, o colaborador visualiza apenas as ordens de serviço atribuídas especificamente a ele.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleTogglePermission('can_view_all_work_orders')}
                          className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer shrink-0 ${
                            permissions.can_view_all_work_orders ? 'bg-brand justify-end' : 'bg-zinc-300 justify-start'
                          }`}
                        >
                          <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
                        </button>
                      </div>

                      {/* Permissão: Visualizar Fornecedores */}
                      <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-2xl flex items-center justify-between">
                        <div className="pr-3">
                          <span className="text-xs font-black text-zinc-800">Acesso à Gestão de Fornecedores</span>
                          <p className="text-[11px] text-zinc-500 mt-0.5">
                            Permite consultar contatos e tabela de compras de distribuidores e fornecedores.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleTogglePermission('can_view_suppliers')}
                          className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer shrink-0 ${
                            permissions.can_view_suppliers ? 'bg-brand justify-end' : 'bg-zinc-300 justify-start'
                          }`}
                        >
                          <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
                        </button>
                      </div>

                    </div>
                  </div>

                  {/* Rodapé com Ações */}
                  <div className="pt-4 border-t border-zinc-100 flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setActiveTab('list')}
                      className="px-4 py-2.5 text-xs font-bold text-zinc-600 hover:bg-zinc-100 rounded-xl transition-colors cursor-pointer"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-6 py-2.5 bg-brand text-white rounded-xl text-xs font-black hover:bg-brand-dark transition-all cursor-pointer shadow-md disabled:opacity-50 flex items-center gap-2"
                    >
                      {isSubmitting ? (
                        <span>Processando...</span>
                      ) : activeTab === 'invite' ? (
                        <>
                          <UserPlus className="h-4 w-4" />
                          <span>Gerar Convite de Acesso</span>
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4" />
                          <span>Salvar Alterações</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}

            </form>
          )}

        </div>
      </div>
    </div>
  );
}
